import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Button, Stack, Alert, CircularProgress,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import {
    extractPayloadFieldsManual,
    hasPayloadFieldsManual,
    payloadFieldTypeLabel,
    resolvePayloadSchemaFormat,
} from "../../utils/payloadSchemaManual";

const ORIGIN_STYLE = {
    INTERNAL: { bgcolor: "#f0f9ff", color: "#0369a1" },
    EXTERNAL: { bgcolor: "#fdf4ff", color: "#7e22ce" },
};

const ViewTransactionTypeDialog = ({ open, transactionType, onClose, onEditRequest }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !transactionType?.id) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/transaction-types/${transactionType.id}`, {
            headers: { "X-Brand": transactionType.brand },
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => { if (!cancelled) setDetail(data); })
            .catch(() => { if (!cancelled) setDetail(transactionType); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, transactionType]);

    const tt = detail || transactionType;
    if (!tt) return null;

    const originStyle = ORIGIN_STYLE[tt.origin] || { bgcolor: "#f5f5f5", color: "#555" };
    const manualFields = extractPayloadFieldsManual(tt);
    const enrichedFields = Array.isArray(tt.payload_fields) ? tt.payload_fields : [];
    const displayFields = enrichedFields.length > 0
        ? enrichedFields
        : Object.entries(manualFields).map(([name, meta]) => ({ name, ...meta }));
    const schemaFormat = resolvePayloadSchemaFormat(tt);
    const hasFields = hasPayloadFieldsManual(tt) || displayFields.length > 0;

    const rows = [
        { label: "Clé (key)", value: tt.key },
        { label: "Marque", value: tt.brand || "Globale" },
        { label: "Format schéma", value: schemaFormat || "— (vide, normal pour sale avant 1ère transaction)" },
        { label: "Identifiant", value: tt.id },
        { label: "Créé le", value: tt.created_at ? new Date(tt.created_at).toLocaleDateString("fr-FR") : "–" },
        { label: "Mis à jour le", value: tt.updated_at ? new Date(tt.updated_at).toLocaleDateString("fr-FR") : "–" },
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.transactionTypes.gradient }}>
                        <EventNoteIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            {tt.name}
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">Détail du type de transaction</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
                ) : (
                    <>
                        <Box display="flex" alignItems="center" gap={1} mb={2.5} px={2} py={1.25}
                            sx={{
                                borderRadius: 2,
                                bgcolor: tt.active ? "#f0fdf4" : "#fef2f2",
                                border: `1px solid ${tt.active ? "#86efac" : "#fca5a5"}`,
                            }}>
                            {tt.active
                                ? <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                                : <CancelIcon sx={{ fontSize: 18, color: "#DC2626" }} />}
                            <Typography fontSize={13} fontWeight={600} color={tt.active ? "#16a34a" : "#DC2626"}>
                                {tt.active ? "Actif" : "Inactif"}
                            </Typography>
                            <Chip label={tt.origin} size="small"
                                sx={{ ml: "auto", fontWeight: 700, fontSize: 11, ...originStyle }} />
                        </Box>

                        {tt.description && (
                            <Typography fontSize={13} color="text.secondary" mb={2}
                                sx={{ fontStyle: "italic", borderLeft: `3px solid ${SECTION_COLORS.transactionTypes.main}50`, pl: 1.5 }}>
                                {tt.description}
                            </Typography>
                        )}

                        <Stack spacing={1} mb={2}>
                            {rows.map(({ label, value }) => (
                                <Box key={label} display="flex" alignItems="flex-start" justifyContent="space-between"
                                    px={2} py={1} sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                                    <Typography fontSize={12} color="text.secondary" fontWeight={500} sx={{ minWidth: 140 }}>{label}</Typography>
                                    <Typography fontSize={12} fontWeight={600} color="#1a1a2e" sx={{ textAlign: "right", wordBreak: "break-all" }}>{value}</Typography>
                                </Box>
                            ))}
                        </Stack>

                        <Box mt={1}>
                            <Typography fontSize={12} fontWeight={700} color={SECTION_COLORS.transactionTypes.main} mb={1}>
                                Champs payload
                            </Typography>
                            {!hasFields ? (
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    {tt.key === "sale"
                                        ? "Aucun champ pour l'instant — normal avant la première transaction sale. Le schéma sera enrichi automatiquement à l'ingestion."
                                        : "Aucun champ payload configuré."}
                                </Alert>
                            ) : (
                                <Stack spacing={0.75}>
                                    {displayFields.map((f) => {
                                        const meta = manualFields[f.name] || f;
                                        return (
                                        <Box key={f.name} px={2} py={1} sx={{ borderRadius: 2, bgcolor: "#f0f9ff", border: "1px solid #bae6fd" }}>
                                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                <Typography fontSize={12} fontWeight={700} color="#1a1a2e">{f.name}</Typography>
                                                <Chip label={payloadFieldTypeLabel(meta.type, meta.format)} size="small" sx={{ fontSize: 9, height: 18 }} />
                                                {f.role && <Chip label={f.role} size="small" sx={{ fontSize: 9, height: 18, bgcolor: "#fef3c7" }} />}
                                                {f.preferred && <Chip label="préféré" size="small" sx={{ fontSize: 9, height: 18, bgcolor: "#dcfce7" }} />}
                                            </Box>
                                            {(f.description || f.conditionField) && (
                                                <Typography fontSize={10} color="text.secondary" mt={0.25}>
                                                    {f.description}
                                                    {f.conditionField ? ` · ${f.conditionField}` : ""}
                                                </Typography>
                                            )}
                                        </Box>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Fermer</Button>
                <Button variant="contained" onClick={() => { onClose(); onEditRequest(tt); }}
                    startIcon={<EditIcon />}
                    sx={{ background: SECTION_COLORS.transactionTypes.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    Modifier
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewTransactionTypeDialog;
